import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useStore } from "@/context/StoreContext";
import heroBg from "@/assets/hero-bg.jpg";

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.14 },
  }),
};

const fadeAnim = {
  hidden: { opacity: 0, y: 26 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.6 + i * 0.14 },
  }),
};

export default function Hero() {
  const { t, scrollToSection } = useStore();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);

  return (
    <section className="hero" id="home" ref={ref} data-testid="hero-section">
      <motion.div className="hero-bg" style={{ y: bgY, scale: bgScale, backgroundImage: `url(${heroBg})` }} />
      <div className="hero-overlay" />

      <div className="hero-content">
        <motion.p
          className="hero-kicker"
          variants={fadeAnim}
          initial="hidden"
          animate="show"
          custom={0}
        >
          {t("hero.kicker")}
        </motion.p>

        <h1>
          {["hero.line1", "hero.line2", "hero.line3"].map((key, i) => (
            <span className="mask" key={key}>
              <motion.span
                variants={lineAnim}
                initial="hidden"
                animate="show"
                custom={i}
                className={i === 2 ? "care" : ""}
              >
                {t(key)}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="hero-copy"
          variants={fadeAnim}
          initial="hidden"
          animate="show"
          custom={1}
        >
          {t("hero.copy1")}
          <br />
          {t("hero.copy2")}
        </motion.p>

        <motion.div
          className="hero-buttons"
          variants={fadeAnim}
          initial="hidden"
          animate="show"
          custom={2}
        >
          <button className="btn btn-dark" data-testid="hero-shop-now-button" onClick={() => scrollToSection("products")}>
            {t("hero.shop")} <span>→</span>
          </button>
          <button className="btn btn-light" data-testid="hero-discover-button" onClick={() => scrollToSection("story")}>
            {t("hero.discover")} <span>→</span>
          </button>
        </motion.div>
      </div>

      <motion.div
        className="hero-side-note"
        initial={{ opacity: 0, rotate: -6, y: 20 }}
        animate={{ opacity: 1, rotate: 0, y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 1.05 }}
      >
        <strong>{t("hero.note1")}<br />{t("hero.note2")}</strong>
        <span>{t("hero.note3")}</span>
      </motion.div>
    </section>
  );
}
